/**
 * Right-hand workspace: header (path, icon actions, Save), the Code editor body,
 * and a status bar reflecting live JSON validity. JSON is collapsible from within
 * the Code view (CodeMirror folding), so there is no separate Tree view.
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo } from "react";
import { Button } from "risa-oasis-ui_v2";
import { CopyIcon } from "../../../../../../shared/svg/copy-icon";
import FormatIcon from "../../../../../../shared/svg/format-icon";
import RefreshIcon from "../../../../../../shared/svg/refresh-icon";
import type { StudioDocument } from "../types";
import type { ParseResult } from "../utils/json";
import CodeEditor from "./CodeEditor";

interface EditorPaneProps {
  document: StudioDocument | null;
  text: string;
  onTextChange: (value: string) => void;
  parsed: ParseResult;
  isDirty: boolean;
  canSave: boolean;
  loading: boolean;
  onFormat: () => void;
  onReset: () => void;
  onSave: () => void;
}

const ACCENT = "#0056D6";

const iconButtonClass = (disabled: boolean) =>
  `flex h-8 w-8 items-center justify-center rounded-md border border-primaryGray-15 bg-white transition-colors ${
    disabled ? "cursor-not-allowed opacity-40" : "hover:bg-primaryGray-15"
  }`;

const EditorPane = ({
  document,
  text,
  onTextChange,
  parsed,
  isDirty,
  canSave,
  loading,
  onFormat,
  onReset,
  onSave,
}: EditorPaneProps) => {
  const summary = useMemo(() => {
    const value = parsed.value;
    if (Array.isArray(value)) {
      return `${value.length} items`;
    }
    if (value && typeof value === "object") {
      return `${Object.keys(value).length} keys`;
    }
    return "";
  }, [parsed.value]);

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("Copied to clipboard"))
      .catch(() => toast.error("Could not copy"));
  };

  if (!document) {
    return (
      <div className="flex h-full flex-1 items-center justify-center rounded-md border border-primaryGray-15 bg-white text-sm text-primaryGray-8">
        {loading
          ? "Loading document..."
          : "Open a document by path to view and edit"}
      </div>
    );
  }

  const formatDisabled = Boolean(parsed.error);

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden rounded-md border border-primaryGray-15 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-primaryGray-15 bg-primaryGray-16 px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-mono text-sm text-primaryGray-2">
            {document.path}
          </span>
          {isDirty && (
            <span className="shrink-0 rounded-full bg-secondaryOrange-12 px-2 py-0.5 text-tiny font-semibold text-secondaryOrange-2">
              Unsaved
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              title="Copy"
              aria-label="Copy"
              onClick={copyToClipboard}
              className={iconButtonClass(false)}
            >
              <CopyIcon width="16" height="16" />
            </button>
            <button
              type="button"
              title="Format"
              aria-label="Format"
              disabled={formatDisabled}
              onClick={onFormat}
              className={iconButtonClass(formatDisabled)}
            >
              <FormatIcon width="16" height="16" stroke={ACCENT} />
            </button>
            <button
              type="button"
              title="Reset to last saved"
              aria-label="Reset to last saved"
              disabled={!isDirty}
              onClick={onReset}
              className={iconButtonClass(!isDirty)}
            >
              <RefreshIcon width="16" height="16" fill={ACCENT} />
            </button>
          </div>
          <Button
            disabled={!canSave}
            onClick={onSave}
            buttonType="primary"
            size="small"
          >
            Save
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <CodeEditor value={text} onChange={onTextChange} />
      </div>

      <div className="flex items-center justify-between border-t border-primaryGray-15 px-4 py-1.5 text-tiny">
        {parsed.error ? (
          <span className="font-medium text-tertiaryRed-4">
            ✕ {parsed.error}
          </span>
        ) : (
          <span className="font-medium text-tertiaryGreen-600">
            ✓ Valid JSON{summary ? ` · ${summary}` : ""}
          </span>
        )}
        {document.updatedAt && (
          <span className="text-primaryGray-8">
            Last saved {new Date(document.updatedAt).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
};

export default EditorPane;
